/**
 * Static templates for the /investors/recently-added/* SEO landing pages.
 * Slugs are matched 1:1 to URL path segments and mapped to a label
 * matched against the `investors.regions` array (case-insensitive,
 * contains-match) via the RPC `get_recently_added_investors`.
 *
 * Titles & descriptions include `{YEAR_MONTH}` which is replaced at
 * render time (and at prerender build time) by the current month/year
 * so prerendered HTML always reflects the latest month.
 */

export interface RecentlyAddedRegion {
  slug: string;
  label: string;
  /** Value passed to the RPC. Matched against any element of investors.regions. */
  regionMatch: string;
  title: string;
  description: string;
  /** Keyword-rich anchor text for cross-region internal linking on /investors. */
  anchor: string;
}

export const RECENTLY_ADDED_INDEX_TITLE =
  "Recently Added Investors by Region – {YEAR_MONTH} | CAPLINK";
export const RECENTLY_ADDED_INDEX_DESCRIPTION =
  "Discover the most recently added venture capital investors worldwide. Browse by region — updated weekly on CAPLINK.";

export const RECENTLY_ADDED_REGIONS: RecentlyAddedRegion[] = [
  {
    slug: "europe",
    label: "Europe",
    regionMatch: "Europe",
    anchor: "European VC Funds",
    title:
      "Recently Added Europe Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Discover the 20 most recently added European venture capital investors and funds. Updated weekly with new VC profiles, fund sizes, and investment focus.",
  },
  {
    slug: "western-europe",
    label: "Western Europe",
    regionMatch: "Western Europe",
    anchor: "Western European VC Funds",
    title:
      "Recently Added Western Europe Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Browse the latest Western European VC funds and investors added to CAPLINK. New profiles from Germany, France, Netherlands, Switzerland and more. Updated weekly.",
  },
  {
    slug: "northern-europe",
    label: "Northern Europe",
    regionMatch: "Northern Europe",
    anchor: "Northern European VC Funds",
    title:
      "Recently Added Northern Europe Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Explore recently added Northern European venture capital investors on CAPLINK. New fund profiles from Sweden, Denmark, Norway, Finland and the Baltics. Updated weekly.",
  },
  {
    slug: "eastern-europe",
    label: "Eastern Europe",
    regionMatch: "Eastern Europe",
    anchor: "Eastern European VC Funds",
    title:
      "Recently Added Eastern Europe Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Find the newest Eastern European VC investors added to CAPLINK. Profiles from Poland, Czech Republic, Romania, Hungary and beyond. Updated weekly.",
  },
  {
    slug: "southern-europe",
    label: "Southern Europe",
    regionMatch: "Southern Europe",
    anchor: "Southern European VC Funds",
    title:
      "Recently Added Southern Europe Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Discover recently added Southern European venture capital funds on CAPLINK. New investor profiles from Spain, Italy, Portugal and Greece. Updated weekly.",
  },
  {
    slug: "north-america",
    label: "North America",
    regionMatch: "North America",
    anchor: "North American Investors",
    title:
      "Recently Added North America Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Find newly added North American venture capital investors on CAPLINK. Active US and Canadian VC funds with global deal interest. Updated weekly.",
  },
  {
    slug: "asia-pacific",
    label: "Asia-Pacific",
    regionMatch: "Asia-Pacific",
    anchor: "Asia-Pacific VC Funds",
    title:
      "Recently Added Asia-Pacific Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Browse recently added Asia-Pacific venture capital investors on CAPLINK. New fund profiles from Australia, Singapore, Japan and Southeast Asia. Updated weekly.",
  },
  {
    slug: "east-asia",
    label: "East Asia",
    regionMatch: "East Asia",
    anchor: "East Asian VC Funds",
    title:
      "Recently Added East Asia Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Explore the latest East Asian VC funds added to CAPLINK. New investor profiles from China, Japan, South Korea and Taiwan. Updated weekly.",
  },
  {
    slug: "southeast-asia",
    label: "Southeast Asia",
    regionMatch: "Southeast Asia",
    anchor: "Southeast Asian VC Funds",
    title:
      "Recently Added Southeast Asia Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Discover recently added Southeast Asian venture capital investors on CAPLINK. New profiles from Singapore, Indonesia, Vietnam and Thailand. Updated weekly.",
  },
  {
    slug: "south-asia",
    label: "South Asia",
    regionMatch: "South Asia",
    anchor: "South Asian VC Funds",
    title:
      "Recently Added South Asia Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Find newly added South Asian venture capital investors on CAPLINK. Active VC funds from India, Bangladesh and Pakistan. Updated weekly.",
  },
  {
    slug: "latin-america",
    label: "Latin America",
    regionMatch: "Latin America",
    anchor: "Latin American VC Funds",
    title:
      "Recently Added Latin America Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Browse the latest Latin American VC investors added to CAPLINK. New fund profiles from Brazil, Mexico, Colombia and Argentina. Updated weekly.",
  },
  {
    slug: "middle-east",
    label: "Middle East",
    regionMatch: "Middle East",
    anchor: "Middle East VC Funds",
    title:
      "Recently Added Middle East Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Discover recently added Middle Eastern venture capital investors and funds on CAPLINK. New profiles from UAE, Saudi Arabia and Israel. Updated weekly.",
  },
  {
    slug: "africa",
    label: "Africa",
    regionMatch: "Africa",
    anchor: "African VC Funds",
    title:
      "Recently Added Africa Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Find the newest African venture capital investors added to CAPLINK. Active VC funds and impact investors across the continent. Updated weekly.",
  },
  {
    slug: "russia-central-asia",
    label: "Russia & Central Asia",
    regionMatch: "Russia & Central Asia",
    anchor: "Russia & Central Asia VC Funds",
    title:
      "Recently Added Russia & Central Asia Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Browse recently added venture capital investors from Russia and Central Asia on CAPLINK. New fund profiles updated weekly.",
  },
  {
    slug: "central-america-caribbean",
    label: "Central America & Caribbean",
    regionMatch: "Central America & Caribbean",
    anchor: "Central America & Caribbean VC Funds",
    title:
      "Recently Added Central America & Caribbean Investors – {YEAR_MONTH} | CAPLINK Investor Database",
    description:
      "Explore newly added venture capital investors from Central America and the Caribbean on CAPLINK. Updated weekly.",
  },
];

export function recentlyAddedRegionFromSlug(
  slug: string | undefined,
): RecentlyAddedRegion | undefined {
  return RECENTLY_ADDED_REGIONS.find((r) => r.slug === slug);
}

export function currentYearMonth(): string {
  return new Date().toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

export function applyYearMonth(template: string): string {
  return template.split("{YEAR_MONTH}").join(currentYearMonth());
}
